import { array } from 'fp-ts';
import { QuotaExceededError } from '@mews/framework';
import { createAction } from '@mews/framework/redux';
import { TextKey } from '@mews-distributor/common';
import { API_MAXIMUM_INTERVAL_TIME_UNITS } from '../../constants';
import {
    getAvailabilityBlockIdFromQuery,
    getCurrencyCode,
    getIsFxMulticurrencyServiceEnabled,
    getLanguageCode,
} from '../../modules/app/selectors';
import { getSelectedHotelConfigurationId } from '../../modules/configuration/selectors';
import { getAvailabilityBlockRateId } from '../../modules/entities/availabilityBlocks/selectors';
import { getEnterpriseTimezone } from '../../modules/entities/enterprises/selectors';
import {
    FETCH_RESERVATION_PRICING_FAILED,
    FETCH_RESERVATION_PRICING_STARTED,
    FETCH_RESERVATION_PRICING_SUCCEEDED,
    FETCH_RESERVATIONS_PRICE_FAILED,
    FETCH_RESERVATIONS_PRICE_STARTED,
    FETCH_RESERVATIONS_PRICE_SUCCEEDED,
} from '../../modules/entities/reservationPrices/actionTypes';
import {
    getCurrentReservationGroupId,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getReservationsList,
} from '../../modules/entities/reservations/selectors';
import { getReservationsWithoutPricingCurrencyCode } from '../../modules/entities/reservations/viewSelectors';
import { getEnterpriseServiceId, getServiceTimeUnit } from '../../modules/entities/services/selectors';
import { getPricingHash } from '../../modules/pricing/getPricingHash';
import { getSelectedCategoryUnavailableOccupancyPricing } from '../../modules/pricing/selectors';
import { getPromotedServiceReservations } from '../../modules/promotedServiceReservations/selectors';
import { EndpointOccupancy } from '../../modules/resourceOrder/occupancyTypes';
import {
    getEndDate,
    getEndTimeUtc,
    getProductIds,
    getRoomId,
    getSelectedHotelId,
    getStartDate,
    getStartTimeUtc,
    getVoucherCode,
} from '../../modules/resourceOrder/selectors';
import { getServicePricingHash } from '../../modules/servicePricing';
import { isValidAvailabilityBlock } from '../../selectors/views/availabilityBlocks';
import { getSelectedEnterpriseTimezone } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { logErrorKey } from '../../services/error/actions';
import { DistributorPromiseThunkAction } from '../../types';
import { toUtc } from '../../utils/dates';
import { isIntervalLimitExceeded } from '../../utils/intervalLimit';
import { getEnterpriseRateIds } from '../apiUtils/enterpriseRateIds';
import { logSentryError } from '../../sentry/sentry';

interface FetchReservationPricingParams {
    endDate: string | undefined;
    enterpriseId: string;
    occupancies: EndpointOccupancy[];
    productIds: string[];
    categoryId: string | undefined;
    startDate: string | undefined;
    voucherCode: string | undefined;
    rateIds: string[];
    startTimeUtc: string | undefined;
    endTimeUtc: string | undefined;
}

const fetchReservationPricing =
    ({
        endDate,
        enterpriseId,
        occupancies,
        productIds,
        categoryId,
        startDate,
        voucherCode,
        rateIds,
        startTimeUtc,
        endTimeUtc,
    }: FetchReservationPricingParams): DistributorPromiseThunkAction =>
    async (dispatch, getState, { apiService, appClockService, loggerService }) => {
        const state = getState();
        const hotelTimezone = getEnterpriseTimezone(state, { enterpriseId });
        const languageCode = getLanguageCode(state);
        const endUtc = hotelTimezone != null && endDate != null ? toUtc(endDate, hotelTimezone) : undefined;
        const startUtc = hotelTimezone != null && startDate != null ? toUtc(startDate, hotelTimezone) : undefined;
        const currencyCode = getCurrencyCode(state);
        const serviceId = getEnterpriseServiceId(state, { enterpriseId });
        const bookingEngineId = getSelectedHotelConfigurationId(state);

        if (serviceId == null) {
            throw new TypeError('Service id is null');
        }

        const timeUnit = getServiceTimeUnit(state, { enterpriseId });
        const payload = {
            hotelId: enterpriseId,
            startDate: startTimeUtc ?? startDate,
            endDate: endTimeUtc ?? endDate,
            productIds,
            roomId: categoryId,
            occupancies,
        };
        const pricingHashes = occupancies.map(({ occupancyData }) =>
            getPricingHash({
                startDate: startTimeUtc ?? startDate,
                endDate: endTimeUtc ?? endDate,
                enterpriseId,
                voucherCode,
                categoryId,
                productIds,
                occupancyData,
            })
        );
        const servicePricingHashes = occupancies.map(({ occupancyData }) =>
            getServicePricingHash({
                occupancyData,
                startUtc: startTimeUtc ?? startUtc,
                endUtc: endTimeUtc ?? endUtc,
                voucherCode,
                enterpriseId,
                serviceId,
                currencyCode,
            })
        );

        if (startDate != null && endDate != null && isIntervalLimitExceeded(startDate, endDate, timeUnit)) {
            const error = new QuotaExceededError(
                `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits. startDate: ${startDate}, endDate: ${endDate}`
            );
            dispatch(
                createAction(FETCH_RESERVATION_PRICING_FAILED, {
                    ...payload,
                    pricingHashes,
                    servicePricingHashes,
                    error,
                })
            );
            dispatch(logErrorKey(TextKey.DateIntervalExceededErrorMessage));
            loggerService.log(
                'fetch-availability-interval-exceeded',
                `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits.`,
                {
                    error,
                }
            );

            return;
        }
        dispatch(createAction(FETCH_RESERVATION_PRICING_STARTED, { ...payload, pricingHashes, servicePricingHashes }));

        const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
        const nowUtc = appClockService.getAppNowUtc();
        const validAvailabilityBlockId =
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            availabilityBlockId && isValidAvailabilityBlock(state, { availabilityBlockId, nowUtc })
                ? availabilityBlockId
                : undefined;

        return apiService
            .getReservationPricing({
                bookingEngineId,
                hotelId: enterpriseId,
                languageCode,
                occupancies,
                productIds,
                roomCategoryId: categoryId,
                startUtc: startTimeUtc ?? startUtc,
                endUtc: endTimeUtc ?? endUtc,
                voucherCode,
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                rateIds: array.isNonEmpty(rateIds) && !validAvailabilityBlockId ? rateIds : undefined,
                availabilityBlockId: validAvailabilityBlockId,
                currencyCodes: [currencyCode],
            })
            .then((response) => {
                dispatch(
                    createAction(FETCH_RESERVATION_PRICING_SUCCEEDED, {
                        ...response,
                        ...payload,
                        pricingHashes,
                        servicePricingHashes,
                    })
                );
                return response;
            })
            .catch((error) => {
                dispatch(
                    createAction(FETCH_RESERVATION_PRICING_FAILED, {
                        ...payload,
                        pricingHashes,
                        servicePricingHashes,
                        error,
                    })
                );
                throw error;
            });
    };

const fetchResourceOrderReservationPricing =
    ({ occupancies }: { occupancies: EndpointOccupancy[] }): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);
        const voucherCode = getVoucherCode(state);
        const categoryId = getRoomId(state);
        const productIds = getProductIds(state);
        const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
        const availabilityBlockRateId =
            availabilityBlockId != null ? getAvailabilityBlockRateId(state, { availabilityBlockId }) : null;
        const rateIds = getEnterpriseRateIds(state, { enterpriseId });
        const startDate = getStartDate(state);
        const endDate = getEndDate(state);
        const startTimeUtc = getStartTimeUtc(state);
        const endTimeUtc = getEndTimeUtc(state);

        return dispatch(
            fetchReservationPricing({
                endDate,
                enterpriseId,
                occupancies,
                productIds,
                categoryId,
                startDate,
                voucherCode,
                rateIds: availabilityBlockRateId != null ? [availabilityBlockRateId] : rateIds,
                startTimeUtc,
                endTimeUtc,
            })
        );
    };

export const fetchResourceOrderReservationPricingWithUnavailableOccupancy =
    (): DistributorPromiseThunkAction => async (dispatch, getState) =>
        dispatch(
            fetchResourceOrderReservationPricing({
                occupancies: getSelectedCategoryUnavailableOccupancyPricing(getState()),
            })
        );

export const fetchReservationsPrice =
    (): DistributorPromiseThunkAction =>
    async (dispatch, getState, { apiService }) => {
        const state = getState();
        const configurationId = getSelectedHotelConfigurationId(state);
        const languageCode = getLanguageCode(state);
        const timezone = getSelectedEnterpriseTimezone(state, {});
        const isFxMulticurrencyServiceEnabled = getIsFxMulticurrencyServiceEnabled(state);

        const reservations = getReservationsList(state);
        const reservationGroupId = getCurrentReservationGroupId(state);
        if (timezone == null) {
            return;
        }
        const finalReservations = reservations.map(
            ({
                orderId: identifier,
                roomId: roomCategoryId,
                startDate,
                endDate,
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                startTimeUtc,
                endTimeUtc,
                productIds,
                rateId,
            }) => ({
                identifier,
                roomCategoryId,
                startUtc: startTimeUtc ?? toUtc(startDate, timezone),
                endUtc: endTimeUtc ?? toUtc(endDate, timezone),
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                productIds,
                rateId,
            })
        );

        const currencyCode = isFxMulticurrencyServiceEnabled
            ? getCurrencyCode(state)
            : getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});

        dispatch(createAction(FETCH_RESERVATIONS_PRICE_STARTED, { reservationGroupId }));

        const promotedServiceReservations = timezone != null ? getPromotedServiceReservations(state, { timezone }) : [];
        const finalPromotedServiceReservations =
            timezone != null
                ? promotedServiceReservations.map(({ orderId, serviceId, startUtc, endUtc, reservationId }) => ({
                      parentReservationIdentifier: orderId,
                      promotedServiceAssignmentId: serviceId,
                      startUtc,
                      endUtc,
                      identifier: reservationId,
                  }))
                : [];
        if (timezone == null) {
            dispatch(
                logSentryError({
                    error: new Error('Cannot fetch reservations price. Timezone is nil.'),
                    extra: { configurationId, reservationGroupId },
                })
            );
        }
        return apiService
            .getReservationsPrice({
                reservations: finalReservations,
                promotedServiceReservations: finalPromotedServiceReservations,
                currencyCode,
                configurationId,
                languageCode,
                settlementCurrencyCode: getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {}),
            })
            .then((response) => {
                dispatch(createAction(FETCH_RESERVATIONS_PRICE_SUCCEEDED, { ...response, reservationGroupId }));
                return response;
            })
            .catch((error) => {
                dispatch(createAction(FETCH_RESERVATIONS_PRICE_FAILED, { error, reservationGroupId }));
                throw error;
            });
    };

export const fetchReservationsCurrencyData =
    ({ currencyCode }: { currencyCode: string }): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const state = getState();

        const pricingEndpointData = getReservationsWithoutPricingCurrencyCode(state, { currencyCode });
        const pricingActions = pricingEndpointData.map(
            async ({
                hotelId: enterpriseId,
                roomId: categoryId,
                endDate,
                startDate,
                occupancies,
                productIds,
                voucherCode,
                startTimeUtc,
                endTimeUtc,
            }) => {
                const rateIds = getEnterpriseRateIds(state, { enterpriseId });
                return dispatch(
                    fetchReservationPricing({
                        endDate,
                        startDate,
                        rateIds,
                        occupancies,
                        productIds,
                        categoryId,
                        enterpriseId,
                        voucherCode,
                        startTimeUtc,
                        endTimeUtc,
                    })
                );
            }
        );
        await Promise.all(pricingActions);
    };
